import structuredClone from '@ungap/structured-clone';
import type { ICombinedProduct } from 'components/CustomizeMenu';
import { getPanelProductReferences, getPanelStyleIndex, isPanelPossibleToCut, isPanelPossibleToFlip } from 'products/panels';
import type { IPanelStyle } from 'redux-store/configuration/configuration.store';
import { getIndexOfFirstImageMatchingType, shoppingItem2ProductId } from 'redux-store/products/products.utils';
import { getProductItemsByIds } from 'redux-store/rootReducer';
import type { IAppSettings } from 'redux-store/settings/settings.store';
import { NavigationSectionsEnum } from 'utils/constants';
import { getTotalAmountForProducts } from 'utils/getTotalAmountForProducts';
import { nonNullable } from 'utils/nonNullable';

import { PanelPattern } from '../Patterns/types/PanelPattern';
import { DoorCalculations } from 'businessLogic/DoorCalculations';
import { DoorWidthEnum } from 'CoreAPI/Components/DoorWidthEnum';
import type { PanelComponent } from 'CoreAPI/Components/PanelComponent';
import { SkyttaComponentIds } from 'CoreAPI/Components/SkyttaComponentIds';
import type SkyttaSizeComponent from 'CoreAPI/Components/SkyttaSizeComponent';
import type CoreAPI from 'CoreAPI/coreApi';
import { getDoorsFramesInScene, getSelectedEntities } from 'CoreAPI/coreSceneQueries';
import { ComponentIds, type Core, type Entity, HierarchyUtils, type LocalTransformComponent } from '@inter-ikea-core/core';
import { ImageTypeNameEnum } from '@inter-ikea-kompis/enums';
import type { IProduct } from '@inter-ikea-kompis/types';

export enum PanelsChangedEventType {
  PANELS_CHANGED = 'panels_changed',
  PANELS_FLIPPED = 'panels_flipped'
}

export interface IPanelItem {
  key: string;
  disabled: boolean;
  product: IProduct;
  productVariations: IProduct[] | undefined;
  panelCanBeFlipped: boolean;
  panelStyleIndex: number;
  panelStyle: IPanelStyle;
  price: number;
}

interface ICombinedPanelProduct extends ICombinedProduct {
  panelStyleIndex: number;
}

const getThinPanels = (coreApi: CoreAPI) => {
  const { selectedDoorLayoutIndex, selectedDoorHeight } = coreApi.productPlacementUtil.userSelections;
  const selectedLayout = DoorCalculations.getDoorLayout(selectedDoorLayoutIndex);

  if (selectedLayout.numThin > 0) {
    const thinItems: string[] = getPanelProductReferences(DoorWidthEnum.Thin, selectedDoorHeight).map((art) => {
      return shoppingItem2ProductId({ id: art, type: 'ART', quantity: 1 });
    });
    return thinItems;
  }

  return [];
};

const getWidePanels = (coreApi: CoreAPI) => {
  const { selectedDoorLayoutIndex, selectedDoorHeight } = coreApi.productPlacementUtil.userSelections;
  const selectedLayout = DoorCalculations.getDoorLayout(selectedDoorLayoutIndex);

  if (selectedLayout.numWide > 0) {
    const wideItems: string[] = getPanelProductReferences(DoorWidthEnum.Wide, selectedDoorHeight).map((art) => {
      return shoppingItem2ProductId({ id: art, type: 'ART', quantity: 1 });
    });
    return wideItems;
  }

  return [];
};

const getActivePanelItemsByDoorLayout = (coreApi: CoreAPI) => {
  const thinItems = getThinPanels(coreApi);
  const wideItems = getWidePanels(coreApi);

  const items: string[] = [...thinItems, ...wideItems];

  return { items, thinItems, wideItems };
};

export const getProductsForPanels = (coreApi: CoreAPI) => {
  const { items, thinItems, wideItems } = getActivePanelItemsByDoorLayout(coreApi);

  const productData = getProductItemsByIds(items);

  const thinProducts = getProductItemsByIds(thinItems);
  const wideProducts = getProductItemsByIds(wideItems);

  return productData
    .map((_product, index) => {
      const bothAreValidDraft: boolean = (!thinItems.length || thinProducts[index]?.valid) && (!wideItems.length || wideProducts[index]?.valid);

      const panelStyleIndex = getPanelStyleIndex((thinProducts[index]?.itemId ?? wideProducts[index]?.itemId)?.split('ART-')[1]);

      if (bothAreValidDraft) {
        return <ICombinedPanelProduct>{
          panelStyleIndex,
          thinProduct: thinProducts[index] || undefined,
          wideProduct: wideProducts[index] || undefined
        };
      }
      return false;
    })
    .filter(Boolean) as ICombinedPanelProduct[];
};

const sortPanels = (allPanelItems: IPanelItem[]) => {
  const filterAndSortDisabled = allPanelItems
    .filter((value) => {
      return value.disabled;
    })
    .sort((a, b) => {
      return a.price - b.price;
    });
  const filterAndSortActive = allPanelItems
    .filter((value) => {
      return !value.disabled;
    })
    .sort((a, b) => {
      return a.price - b.price;
    });

  return [...filterAndSortActive, ...filterAndSortDisabled];
};

export const getDoorsInScope = (core: Core): Entity[] => {
  const selectedEntities = getSelectedEntities(core);
  return selectedEntities.length ? selectedEntities : getDoorsFramesInScene(core);
};

export const getDoorWidths = (doorsInScope: Entity[]): DoorWidthEnum[] => {
  return doorsInScope.reduce((widths: DoorWidthEnum[], door: Entity) => {
    const sizeComponent = door.getComponent<SkyttaSizeComponent>(SkyttaComponentIds.SkyttaSizeComponentId);

    if (sizeComponent) {
      widths.push(sizeComponent.width);
    }

    return widths;
  }, []);
};

export const getPanelStyleFromEntity = (panel: Entity) => {
  const index = panel.getComponent<PanelComponent>(SkyttaComponentIds.PanelComponentId)?.styleIndex;

  const localRotation = panel.getComponent<LocalTransformComponent>(ComponentIds.LOCAL_TRANSFORM_COMPONENT_ID)?.transform.rotation;

  const isFlipped = localRotation?.y === 1 || localRotation?.y === -1;

  if (!index) {
    return null;
  }

  return {
    index,
    isFlipped
  };
};

export const comparePanelStyles = (p1: IPanelStyle, p2: IPanelStyle): boolean => {
  return p1.index === p2.index && p1.isFlipped === p2.isFlipped;
};

export const replacePanelsInScope = (coreApi: CoreAPI, panelStyle: IPanelStyle) => {
  coreApi.productPlacementUtil.userSelections.selectedPanelPattern = PanelPattern.Unknown;

  // When a door is selected, only change the panels on that door
  const selectedEntities = getSelectedEntities(coreApi.core);
  if (selectedEntities.length > 0) {
    selectedEntities.forEach((entity) => {
      if (entity.hasComponent(SkyttaComponentIds.SlidingDoorFrameComponentId)) {
        coreApi.productPlacementUtil.replacePanelsInDoor(entity, panelStyle);
      }
    });
    return;
  }

  coreApi.productPlacementUtil.removeAllPanels();
  coreApi.productPlacementUtil.addUniformPanels(panelStyle);
};

export const getPanelsInScopeStyleIndexes = (doorsInScope: Entity[]) => {
  return doorsInScope.flatMap((doorFrame) => {
    return HierarchyUtils.children(doorFrame).map((child) => {
      return child.getComponent<PanelComponent>(SkyttaComponentIds.PanelComponentId)?.styleIndex;
    });
  });
};

export const getPanelsInScope = (selectedEntities: Entity[]): IPanelStyle[] => {
  const panels: Entity[] = selectedEntities.reduce((acc: Entity[], entity) => {
    if (entity.hasComponent(SkyttaComponentIds.PanelComponentId)) {
      acc.push(entity);
    }

    if (entity.hasComponent(SkyttaComponentIds.SlidingDoorFrameComponentId)) {
      HierarchyUtils.children(entity).map((panel) => {
        return acc.push(panel);
      });
    }

    return acc;
  }, []);

  return panels
    .map((panel) => {
      return getPanelStyleFromEntity(panel);
    })
    .filter(nonNullable);
};

export const getDataForPanelItems = (validProducts: ICombinedPanelProduct[], coreApi: CoreAPI, settings: IAppSettings) => {
  const panelItems: IPanelItem[] = validProducts.reduce((panels: IPanelItem[], combinedProduct: ICombinedPanelProduct) => {
    const { thinProduct, wideProduct, panelStyleIndex } = combinedProduct;

    const product = thinProduct || wideProduct;

    if (product && product.content) {
      const bothAreValid: boolean = (!thinProduct || thinProduct?.valid) && (!wideProduct || wideProduct?.valid);

      const selectedPanelsCanNotBeCut = !isPanelPossibleToCut(panelStyleIndex);
      const howMuchToCut = coreApi.productPlacementUtil.howMuchToCut();

      const disableDoorsThatCanNotBeCut = howMuchToCut > 0 && selectedPanelsCanNotBeCut;
      const disabled = !bothAreValid || disableDoorsThatCanNotBeCut;

      const panelCanBeFlipped = isPanelPossibleToFlip(combinedProduct.panelStyleIndex);

      const productVariants: IProduct[] = [];
      if (thinProduct) productVariants.push(thinProduct);
      if (wideProduct) productVariants.push(wideProduct);
      const productVariations = productVariants.length > 1 ? productVariants : undefined;

      const key = `${NavigationSectionsEnum.Panels}-${panelStyleIndex}`;

      const price = getTotalAmountForProducts(productVariants, settings);

      const panel: IPanelItem = {
        key,
        product,
        productVariations,
        panelStyleIndex,
        panelStyle: { index: panelStyleIndex, isFlipped: false },
        panelCanBeFlipped,
        disabled,
        price
      };

      panels.push(panel);

      if (panelCanBeFlipped) {
        // Remove the first image of the wanted type since it's the front side image. Currently only way to display the back side image
        // Discussion with Kompis if this can be solved better
        // https://i-p-e-x.slack.com/archives/CEY6PNS0Y/p1713439094601039
        const backSideProduct = structuredClone(product);
        if (panelCanBeFlipped && backSideProduct.content?.image?.length) {
          const index = getIndexOfFirstImageMatchingType(backSideProduct.content.image, [ImageTypeNameEnum.qualityProductPicture]);

          if (index) {
            backSideProduct.content.image.splice(index, 1);
          }
        }

        panels.push({
          ...panel,
          key: `${key}-backside`,
          product: backSideProduct,
          panelStyle: { index: panelStyleIndex, isFlipped: true }
        });
      }
    }
    return panels;
  }, [] as IPanelItem[]);

  return sortPanels(panelItems);
};

/**
 * Reverses order since panels are placed from bottom to top in core
 */
export const getPanelsInDoorFrameSortedFromTopToBottom = (doorFrame: Entity) => {
  return HierarchyUtils.children(doorFrame).sort((a, b) => {
    return (
      b.getComponentUnsafe<PanelComponent>(SkyttaComponentIds.PanelComponentId).verticalPositionInDoorFrame -
      a.getComponentUnsafe<PanelComponent>(SkyttaComponentIds.PanelComponentId).verticalPositionInDoorFrame
    );
  });
};
